const WORKFLOW_OPS_OVERRIDE_KEY = "feature.workflowOps";

const WORKFLOW_OPS_CAPABILITIES = [
  "WorkflowOpsRead",
  "WorkflowOpsTriage",
  "WorkflowOpsExecute",
  "WorkflowOpsRepair",
  "WorkflowOpsSuperAdmin",
];

const normalizeClaims = (authUser) => authUser?.Claims || [];
const hasAdminAccess = (authUser) => normalizeClaims(authUser).includes("admin.access");

const normalizeCapabilityToken = (value) =>
  `${value || ""}`.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const capabilityTokenSet = new Set(
  WORKFLOW_OPS_CAPABILITIES.map(normalizeCapabilityToken)
);

const readLocalOverride = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const value = (window.localStorage.getItem(WORKFLOW_OPS_OVERRIDE_KEY) || "")
    .trim()
    .toLowerCase();

  if (!value) return null;
  if (["1", "true", "on", "enabled"].includes(value)) return true;
  if (["0", "false", "off", "disabled"].includes(value)) return false;

  return null;
};

export const getWorkflowOpsCapabilities = (authUser) => {
  if (hasAdminAccess(authUser)) {
    return [...WORKFLOW_OPS_CAPABILITIES];
  }

  const claims = normalizeClaims(authUser);

  return claims.filter((claim) =>
    capabilityTokenSet.has(normalizeCapabilityToken(claim))
  );
};

export const hasWorkflowOpsFeatureFlag = (authUser) => {
  if (hasAdminAccess(authUser)) {
    return true;
  }

  const localOverride = readLocalOverride();
  if (typeof localOverride === "boolean") {
    return localOverride;
  }

  const envFlag = `${process.env.REACT_APP_ENABLE_WORKFLOW_OPS || ""}`
    .trim()
    .toLowerCase() === "true";

  return envFlag || getWorkflowOpsCapabilities(authUser).length > 0;
};

export const hasWorkflowOpsAccess = (authUser) => {
  return hasAdminAccess(authUser);
};

export const canViewDangerousRail = (authUser) => {
  if (hasAdminAccess(authUser)) {
    return true;
  }

  const claims = normalizeClaims(authUser).map(normalizeCapabilityToken);

  return (
    claims.includes(normalizeCapabilityToken("WorkflowOpsRepair")) ||
    claims.includes(normalizeCapabilityToken("WorkflowOpsSuperAdmin"))
  );
};

export const canViewAdvancedWorkbench = (authUser) => {
  return hasAdminAccess(authUser);
};

export const hasWorkflowOpsCapability = (authUser, capabilityName) => {
  if (hasAdminAccess(authUser)) {
    return true;
  }

  const claims = normalizeClaims(authUser).map(normalizeCapabilityToken);
  if (claims.includes(normalizeCapabilityToken("WorkflowOpsSuperAdmin"))) {
    return true;
  }
  return claims.includes(normalizeCapabilityToken(capabilityName));
};

export const canExecutePreviewedCommand = (authUser, preview) => {
  const requiredCapability = preview?.RequiredCapability || preview?.requiredCapability;
  if (!requiredCapability) {
    return false;
  }

  return hasWorkflowOpsCapability(authUser, requiredCapability);
};

export const WORKFLOW_OPS_OVERRIDE_STORAGE_KEY = WORKFLOW_OPS_OVERRIDE_KEY;
export const WORKFLOW_OPS_CAPABILITY_NAMES = WORKFLOW_OPS_CAPABILITIES;
